"use client";

// WO-MEMBER-STATE-PROPAGATION-001 · T1 — THE PRODUCT-CARD FARE PAIR, MADE TO READ THE SESSION.
//
// FOUNDER, on :3021 with the Member Preview panel showing "Signed in as vendor1.user1@example.com":
// the fares still rendered BOTH prices side by side, unstruck — the signed-out presentation.
//
// ── WHAT THE MEASUREMENT ACTUALLY FOUND, because it is not what the work order assumed ──────────
//
// The WO's first hypothesis is that the fare surfaces read a different store from the Member Preview
// panel, or never re-render on auth change. Walked on :3021, signed in and signed out, both are
// false for TRANSPORT: `BookingResults.tsx` already reads `useCartStore(s => s.session)` and already
// implements the ruled behaviour at line 558 (`signedIn ? ["member"] : ["guest","member"]`). Measured
// live on `/mountain-whistler/booking/schedule/depart/vancouver-to-whistler?startDate=…`:
//     signed in  →  9 price boxes, every one "Member Standard $33"
//     signed out → 18 price boxes, "Guest Standard $34" + "Member Standard $33", member gated
// and it flips on the toggle with no reload. That surface was never the defect.
//
// THE DEFECT IS THE PRODUCT CARDS, and they are a different shape of problem entirely: they are not
// a component at all. On the Phase-2 render every card arrives as ONE concatenated string inside a
// single `listitem` block —
//
//     "Private Axe Throwing – 1 HourExperience…Guest Price$38 CADMember Price$35 CADDesired Date:…"
//
// There is no element around either price, which is why nothing could style one: you cannot strike
// through a substring of a text node. `QuickViewLayer`'s modal has the same pair as real elements but
// reads no session at all (11 price references, 0 session references — same for
// `MarketplaceSupplierDetail`, `CartPage` and `ExportCheckout`).
//
// So this component does two things: it gives the pair a DOM structure, and it makes that structure
// read the session. It is a CLIENT component deliberately — `ContentPage` is server-rendered, and a
// server-rendered price cannot flip when the user signs in without the reload the WO forbids.
//
// ── THE RULED PRESENTATION ──────────────────────────────────────────────────────────────────────
//   signed in  → the guest price is STRUCK THROUGH beside the member price (product cards)
//   signed out → unchanged from today: both prices, neither struck ("guest presentation")
// Transport is the other ruled shape (member price ONLY when signed in) and already ships it.
//
// `data-member-price` / `data-price-role` are the gate's handles. They are attributes rather than
// class names because a class here would have to survive the captured CSSOM, and this markup renders
// inside pages that inline live's own stylesheet.

import { isSignedIn, useCartStore } from "@/lib/cart-store";

/** `Guest Price$38 CADMember Price$35 CAD` — live's own concatenation, currency optional per side. */
// WO-MEMBER-STATE-PROPAGATION-001 · P0 FIX — the parser now lives in `@/lib/price-pair`, which carries
// NO `"use client"`, because `ContentPage` renders on the SERVER and calling a client-module export
// from there is a hard runtime error (it 500'd /wssf and /wssf/learn-more). `"use client"` marks a
// module client-only in its entirety, so a pure helper sitting beside a component is quarantined with
// it. Re-exported here so every existing client import keeps working unchanged.
export { hasPricePair, splitPricePair } from "@/lib/price-pair";

export default function MemberPrice({ guest, member, currency }: { guest: string; member: string; currency?: string }) {
  // ONE store, the same one the Member Preview panel writes and BookingResults reads. A second
  // source of session truth is the whole defect this work order is about.
  const signedIn = useCartStore((s) => isSignedIn(s.session));
  const cur = currency ? ` ${currency}` : "";

  return (
    <span data-member-price={signedIn ? "member" : "guest"} style={{ display: "inline-flex", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
      <span data-price-role="guest" style={{ whiteSpace: "nowrap" }}>
        <span style={{ fontSize: 11, color: "#6b7280", marginRight: 4 }}>Guest Price</span>
        <strong
          style={
            signedIn
              ? // struck through, and de-emphasised so the member price is the one that reads as the
                // price. `line-through` alone on same-weight text still competes with it.
                { textDecoration: "line-through", color: "#9ca3af", fontWeight: 400 }
              : undefined
          }
        >
          ${guest}
          {cur}
        </strong>
      </span>
      <span data-price-role="member" style={{ whiteSpace: "nowrap" }}>
        <span style={{ fontSize: 11, color: "#6b7280", marginRight: 4 }}>Member Price</span>
        <strong style={{ color: "#e87722" }}>
          ${member}
          {cur}
        </strong>
      </span>
    </span>
  );
}

